import React, { Suspense, useState } from "react";
import { SensorKitFilters, useSensorKits } from "@/sensorKits/api";
import { Card } from "@/components/ui/card";
import { useSensorKitHealthFilters } from "./filters/useSensorKitHealthFilters";
import { FilterBar } from "./filters/FilterBar";
import { SensorKitTablePanel } from "./table/SensorKitTablePanel";
import { SensorBoxPlot } from "./boxplot/SensorBoxPlot";
import { BoxplotSortingControls } from "./boxplot/BoxplotSortingControls";
import { DateRangeContextProvider } from "@/dateRanges/DateRangeContext";
import { CalendarDateRangePicker } from "@/components/date-range-picker";

const AllSensorKitsPanel = ({ filters }: { filters: SensorKitFilters }) => {
  const { data: sensorKits } = useSensorKits(filters);

  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-2">All Sensor Kits</h3>
      <p>There are {sensorKits.count} sensor kits matching your filters</p>
    </Card>
  );
};

const OfflineSensorKitsPanel = ({ filters }: { filters: SensorKitFilters }) => (
  <SensorKitTablePanel
    title="Offline Sensor Kits"
    titleColor="text-red-600"
    filters={filters}
    additionalFilters={{ is_offline: true }}
    alertFilterType="offline"
  />
);

const OnlineSensorKitsPanel = ({ filters }: { filters: SensorKitFilters }) => (
  <SensorKitTablePanel
    title="Online Sensor Kits"
    titleColor="text-green-600"
    filters={filters}
    additionalFilters={{ is_offline: false }}
  />
);

const MissingSensorDataPanel = ({ filters }: { filters: SensorKitFilters }) => (
  <SensorKitTablePanel
    title="Missing Sensor Data"
    titleColor="text-orange-600"
    filters={filters}
    additionalFilters={{ is_offline: false, has_missing_sensor_data: true }}
    alertFilterType="missing_data"
  />
);

const OutOfSpecSensorDataPanel = ({ filters }: { filters: SensorKitFilters }) => (
  <SensorKitTablePanel
    title="Out of Spec Sensor Data"
    titleColor="text-yellow-600"
    filters={filters}
    additionalFilters={{ is_offline: false, has_out_of_spec_sensor_data: true }}
    alertFilterType="out_of_spec"
  />
);

export const SensorKitHealthNew = () => {
  const {
    modelFilter,
    setModelFilter,
    versionFilter,
    setVersionFilter,
    hasCurrentInstallationFilter,
    setHasCurrentInstallationFilter,
    filters,
  } = useSensorKitHealthFilters();

  const [orderBy, setOrderBy] = useState("median_data");
  const [orderDirection, setOrderDirection] = useState("DESC");

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Sensor Kit Health</h1>

      <FilterBar
        modelFilter={modelFilter}
        onModelChange={setModelFilter}
        versionFilter={versionFilter}
        onVersionChange={setVersionFilter}
        hasCurrentInstallationFilter={hasCurrentInstallationFilter}
        onInstallationStatusChange={setHasCurrentInstallationFilter}
      />

      <div className="space-y-4">
        <Suspense fallback={<div>Loading all sensor kits...</div>}>
          <AllSensorKitsPanel filters={filters} />
        </Suspense>

        <Suspense fallback={<div>Loading online sensor kits...</div>}>
          <OnlineSensorKitsPanel filters={filters} />
        </Suspense>

        <Suspense fallback={<div>Loading offline sensor kits...</div>}>
          <OfflineSensorKitsPanel filters={filters} />
        </Suspense>

        <Suspense fallback={<div>Loading missing sensor data...</div>}>
          <MissingSensorDataPanel filters={filters} />
        </Suspense>

        <Suspense fallback={<div>Loading out of spec sensor data...</div>}>
          <OutOfSpecSensorDataPanel filters={filters} />
        </Suspense>

        <DateRangeContextProvider>
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <BoxplotSortingControls
                orderBy={orderBy}
                onOrderByChange={setOrderBy}
                orderDirection={orderDirection}
                onOrderDirectionChange={setOrderDirection}
              />
              <CalendarDateRangePicker showExtraOptions={false} />
            </div>
          </Card>

          <SensorBoxPlot
            filters={filters}
            queryType="TEMPERATURE_INSIDE_ENCLOSURE"
            orderBy={orderBy}
            orderDirection={orderDirection}
          />
          <SensorBoxPlot
            filters={filters}
            queryType="RELATIVE_HUMIDITY"
            orderBy={orderBy}
            orderDirection={orderDirection}
          />
          <SensorBoxPlot
            filters={filters}
            queryType="WIFI_RSSI"
            orderBy={orderBy}
            orderDirection={orderDirection}
          />
        </DateRangeContextProvider>
      </div>
    </div>
  );
};
